/**
 * Utility for working with short IDs in URLs.
 *
 * Short IDs are 8-character alphanumeric strings used in place of UUIDs
 * in user-facing URLs for readability (e.g., /invoices/kR9x4mNp).
 *
 * UUID format: 8-4-4-4-12 hex chars with dashes (36 chars total)
 * Short ID: 8 alphanumeric chars (no ambiguous chars like 0, O, 1, l, I)
 */

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Check if a string looks like a UUID */
export function isUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

/** Check if a string looks like a short ID (not a UUID) */
export function isShortId(value: string): boolean {
  return value.length <= 12 && !UUID_REGEX.test(value);
}

/**
 * Build a Supabase query filter that works with both short IDs and UUIDs.
 * Use this during the migration period so old bookmarked URLs still work.
 */
export function filterByIdOrShortId<
  T extends {
    eq: (col: string, val: string) => T;
  },
>(query: T, paramValue: string, idColumn = 'id', shortIdColumn = 'short_id'): T {
  if (isUUID(paramValue)) {
    return query.eq(idColumn, paramValue);
  }
  return query.eq(shortIdColumn, paramValue);
}
